<script>
import { mapGetters } from 'vuex'
import messages from '@/mixins/messages'
import ChargeResource from '@/resources/charge'
import CalendarResource from '@/resources/calendar'
import LocationResource from '@/resources/location'
import TermsOfServiceLink from './TermsOfServiceLink.vue'
import PaymentMethod from './PaymentMethod.vue'

export default {
    name: 'ApexClubPitch',
    mixins: [messages],
    components: {
        TermsOfServiceLink,
        PaymentMethod
    },
    props: {
        sessions: {
            type: [String, Number],
            required: false
        }
    },
    computed: {
        ...mapGetters(['userResource']),
        locationCurrencySymbol() {
            return this.selectedLocation?.attributes?.currency_symbol || '$'
        },
        mappedSimTypes() {
            const indexedTypes = {}

            for (let resource of this.calendarResources) {
                const attrs = resource.attributes
                
                if (this.userResource.attributes.location !== attrs.location) {
                    continue
                }

                if ('undefined' === typeof indexedTypes[attrs.type]) {
                    indexedTypes[attrs.type] = {
                        costCents: attrs.cost_cents,
                        costUnit: attrs.cost_unit,
                        typeName: attrs.type_name
                    }
                }
            }
            
            return indexedTypes
        },
        isIntroRate() {
            return this.racesIntroCostCents && this.racesPerMonth == 1
        },
        racesPerMonthMinAbsolute() {
            if (this.racesIntroCostCents) {
                return 1
            }

            return this.racesPerMonthMin
        },
        subscriptionOptionsExist() {
            if (this.locationResources && this.locationResources.length > 0) {
                for (let key in this.locationResources) {
                    if (!!parseFloat(this.locationResources[key].attributes.sales_tax_rate_percent)) {
                        return true
                    }
                }
            }

            return false
        }
    },
    data() {
        return {
            loading: true,
            credits: 1000,
            creditsTotalCents: 0,
            creditsQuotedCents: 0,
            creditsDiscountRate: 0,
            racesPerMonthMin: 4,
            racesPerMonth: 4,
            racesPerMonthDebounceTimeout: 300,
            racesPerMonthDebounceTimeoutId: null,
            racesIntroCostCents: 0,
            selectedRigType: '',
            selectedLocation: null,
            salesTaxInclude: false,
            userCardDetails: {},
            calendarResources: [],
            locationResources: [],
            intervalCalculateCreditsDebounced: null
        }
    },
    watch: {
        selectedRigType() {
            this.calculateCredits()
        },
        salesTaxInclude() {
            this.calculateCredits()
        },
        selectedLocation() {
            if (this.salesTaxInclude) {
                this.calculateCredits()
            }
        },
        racesPerMonth(val, prevVal) {
            if (val < this.racesPerMonthMin) {
                this.$nextTick(() => {
                    if (this.racesIntroCostCents) {
                        this.racesPerMonth = (val > prevVal) ? this.racesPerMonthMin : 1
                    } else {
                        this.racesPerMonth = this.racesPerMonthMin
                    }

                    this.calculateCreditsDebounced()
                })
            } else {
                this.calculateCreditsDebounced()
            }
        }
    },
    methods: {
        async onSubmit() {
            try {
                this.loading = true
                await ChargeResource.creditEnrollUser(this.isIntroRate ? -1 : this.credits)
                this.$store.commit('isApexClub', true)
                this.$emit('success', this.credits)
                this.$root.$bvToast.toast(`Congratulations! 🥳 🎉 Welcome to ${this.$appConfig('APEX_CLUB_NAME', 'Apex Club')}! Head over to Reservations to book a race`, {
                    title: 'Congrats!',
                    autoHideDelay: 15000,
                    variant: 'info',
                    solid: true,
                    to: 'reservations'
                })
            } catch(e) {
                throw this.axiosExtract(e)
            } finally {
                this.loading = false
            }
        },
        async calculateCredits() {
            this.loading = true
            const centsEstimated = this.selectedRigType.costCents * this.racesPerMonth
            this.credits = this.centsToCredits(centsEstimated)

            if (this.salesTaxInclude) {
                const taxRateFloat = parseFloat(this.selectedLocation.attributes.sales_tax_rate_percent) / 100
                const diffCents = Math.ceil(taxRateFloat * centsEstimated)
                this.credits += this.centsToCredits(diffCents)
            }

            const result = await ChargeResource.creditPurchaseQuote(this.isIntroRate ? -1 : this.credits)
            this.creditsQuotedCents = result.quoted_cents
            this.creditsTotalCents = result.total_cents
            this.credits = result.credits_amount
            this.creditsDiscountRate = parseInt(result.discount_percent)
            this.loading = false
        },
        calculateCreditsDebounced(timeoutMs = 500) {
            if (this.timeoutCalculateCreditsDebounced) {
                clearTimeout(this.timeoutCalculateCreditsDebounced)
            }

            this.timeoutCalculateCreditsDebounced = setTimeout(() => {
                this.calculateCredits()
                this.timeoutCalculateCreditsDebounced = null
            }, timeoutMs);
        },
        async onCardAdded() {
            this.userCardDetails = await this.userResource.getCard()
        },
    },
    async mounted() {
        this.userCardDetails = await this.userResource.getCard()

        const [calendarResources, locationResources] = await Promise.all([
            CalendarResource.all(),
            LocationResource.all()
        ])

        this.calendarResources = calendarResources
        this.locationResources = locationResources
        this.selectedRigType = this.mappedSimTypes[this.calendarResources[0].attributes.type]
        this.selectedLocation = (this.locationResources.length > 0) ? this.locationResources.find((location) => location.id == this.userResource.attributes.location) : this.locationResources[0]

        this.racesIntroCostCents = parseInt(this.selectedLocation.attributes.subscription_intro_cost_cents) || 0
        this.racesPerMonthMin = parseInt(this.selectedLocation.attributes.subscription_min_races) || 4
        this.racesPerMonth = (this.racesIntroCostCents) ? 1 : this.racesPerMonthMin
        
        this.loading = false

        if (this.sessions) {
            this.$nextTick(() => {
                this.racesPerMonth = this.sessions
            })
        }
    }
}
</script>

<template>
    <div>
        <!--<p>Apex Club members receive a <strong>20% discount on all racing sessions</strong> booked with their credits, allowing you to save even more on your virtual racing experience. And, as a special perk, Apex Club members can make special requests for car and track combinations to create a truly personalized racing experience. Don't miss out on the excitement, join the Apex Club today and start your virtual racing journey with the best sim racing has to offer!</p>-->
        <b-row v-if="userResource">
            <b-col>
                <b-form @submit.prevent="onSubmit">
                    <b-card class="border-0" no-body>
                        <b-form-group label="Wie viele Sessions pro Monat?" :description="`Mindestens ${racesPerMonthMinAbsolute == 1 ? '1 Session' : (racesPerMonthMinAbsolute + ' Sessions')} pro Monat sind für den Beitritt erforderlich`">
                            <b-form-spinbutton min="1" v-model="racesPerMonth" max="100" />
                        </b-form-group>
                        <div v-if="isIntroRate">
                            <p><strong>Einführungspreis!</strong> Erhalte 1 Session pro Monat sowie alle Vorteile einer Mitgliedschaft im {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} zu diesem Sonderpreis. <u>Nur am Standort <resource-attribute :resource="userResource" attribute="location.name" /> gültig</u>.</p>
                        </div>
                        <div v-else>
                            <b-form-group label="Welche Art von Simulator bevorzugst du?">
                                <b-form-radio-group v-model="selectedRigType">
                                    <b-form-radio v-for="(simType, i) in mappedSimTypes" :key="i" :value="simType">{{ simType.typeName }}</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                            <hr />
                        </div>
                        <div v-if="!loading">
                            <p>Für eine wiederkehrende Zahlung von <strong>{{ locationCurrencySymbol }}{{ moneyFormat(creditsQuotedCents) }} pro Monat</strong> erhältst du jeden Monat <strong><b-icon icon="currency-exchange" /> {{ credits }} Credits</strong>. Bei den heutigen Preisen <strong>reicht das aus, um einen {{ selectedRigType.typeName }}-Simulator {{ (racesPerMonth == 1) ? '1 Mal' : `${racesPerMonth} Mal` }} pro Monat zu buchen.</strong></p>
                            <p>Das entspricht einer <strong class="text-success">Ersparnis von {{ creditsDiscountRate }} %</strong> gegenüber unseren normalen Preisen! <strong>Für Mitglieder des {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} entfallen außerdem die Buchungsgebühren!</strong> Dazu kommen viele weitere Vorteile!</p>
                            
                            <slot name="default">
                                <p class="text-muted"><small>(Ja, du kannst deine Sessions für Freunde und Familie nutzen!)</small></p>
                            </slot>

                            <div v-if="subscriptionOptionsExist">
                                <b-form-group label="Umsatzsteuer in das Abonnement einbeziehen?">
                                    <b-form-radio-group v-model="salesTaxInclude">
                                        <b-form-radio :value="false">Nein</b-form-radio>
                                        <b-form-radio :value="true">Ja</b-form-radio>
                                    </b-form-radio-group>
                                </b-form-group>
                                <b-card v-if="salesTaxInclude && locationResources.length > 1" header="Steuersatz eines Standorts auswählen" class="mb-3">
                                    <b-form-select v-model="selectedLocation">
                                        <b-form-select-option v-for="(location, i) in locationResources" :key="i" :value="location">{{ location }} ({{ location.attributes.sales_tax_rate_percent }}%)</b-form-select-option>
                                    </b-form-select>
                                </b-card>
                            </div>

                            <div v-if="!userCardDetails || !Object.keys(userCardDetails).length">
                                <hr />
                                <payment-method cols-lg="12" :user-resource="userResource" @success="onCardAdded">
                                    <b-alert variant="success" show><b-icon icon="lock-fill" /> Deine Karte wird noch nicht belastet</b-alert>
                                </payment-method>
                            </div>
                            
                            <div v-else>
                                <!-- <p>Plus, being an Apex Club member enables <strong>Special Requests</strong> with your bookings!</p> -->
                                <b-alert variant="info" show>Deine <strong>{{ userCardDetails.brand }} mit den Endziffern {{ userCardDetails.last4 }}</strong> wird ab heute <strong>monatlich mit {{ locationCurrencySymbol }}{{ moneyFormat(creditsTotalCents) }}</strong> belastet</b-alert>
                                <small class="text-muted"><terms-of-service-link /></small>
                                <messages />
                                <b-button type="submit" variant="primary" class="col-sm-12 mt-3" :disabled="loading">Jetzt dem {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} beitreten</b-button>
                            </div>
                        </div>

                        <div v-else class="text-center text-muted">
                            <b-spinner type="grow" label="Wird geladen..." class="mt-5 mb-5" />
                        </div>
                    </b-card>
                </b-form>
            </b-col>
        </b-row>

        <div v-else>
            <b-skeleton-wrapper>
                <b-skeleton width="85%"></b-skeleton>
                <b-skeleton width="55%"></b-skeleton>
                <b-skeleton width="70%"></b-skeleton>
            </b-skeleton-wrapper>
        </div>
    </div>
</template>

<style lang="scss">
</style>
